import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CheckCircle2, Loader2 } from 'lucide-react';
import api from '../../lib/api';
import { fehlertext } from '../../lib/fehlertext';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { SchrittLeiste } from './SchrittLeiste';
import { AppAnleitung, NummernListe } from './AppAnleitung';
import { Schluessel } from './Schluessel';
import { CodesAnzeige } from './CodesAnzeige';
import { CodeFelder, Fett } from './CodeFelder';
import { GeraetMerken } from './GeraetMerken';
import { merkerFuer, merkerSpeichern } from '../../lib/geraet';
import { handyArt, VERBINDEN_SCHRITTE, HANDY_HINWEIS, ZAHL_TEXT, UHRZEIT_HINWEIS, GESCHAFFT } from '../../lib/zweiFaktor';

const TITEL = { 1: 'Schritt 1 von 3: App installieren', 2: 'Schritt 2 von 3: Mit der App verbinden', 3: 'Schritt 3 von 3: Zahl eintippen', 4: 'Wiederherstellungscodes' };
const FALSCH = 'Der Code ist nicht richtig. Bitte versuchen Sie es erneut.';
const START_FEHLER = 'Die Einrichtung konnte nicht gestartet werden.';

const QrBild = ({ src }) => (
  <div className="flex justify-center">
    <img src={src} alt="QR-Code für die Authenticator-App" className="h-56 w-56 bg-white sm:h-64 sm:w-64" data-testid="zwei-faktor-qr" />
  </div>
);

const LinkKnopf = ({ onClick, children, testid }) => (
  <button type="button" onClick={onClick} className="text-sm text-blue-700 hover:text-blue-900 hover:underline" data-testid={testid}>{children}</button>
);

const Verbinden = ({ daten, art }) => {
  const [offen, setOffen] = useState(false);
  if (art === 'pc') {
    return (
      <div className="space-y-4">
        <NummernListe punkte={VERBINDEN_SCHRITTE} testid="zwei-faktor-verbinden-schritte" />
        <QrBild src={daten.qr_code} />
        <p className="text-center text-sm text-slate-600">
          Scannen klappt nicht?{' '}
          <LinkKnopf onClick={() => setOffen(!offen)} testid="zwei-faktor-schluessel-zeigen"><strong>Diesen Schlüssel eintippen</strong></LinkKnopf>
        </p>
        {offen && <Schluessel schluessel={daten.schluessel} />}
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900" data-testid="zwei-faktor-handy-hinweis">{HANDY_HINWEIS}</div>
      {art === 'android' && (
        <Button asChild className="w-full bg-blue-800 hover:bg-blue-900">
          <a href={daten.einrichtungs_link} data-testid="zwei-faktor-app-verbinden">Mit Authenticator-App verbinden</a>
        </Button>
      )}
      <Schluessel schluessel={daten.schluessel} />
      <LinkKnopf onClick={() => setOffen(!offen)} testid="zwei-faktor-qr-zeigen">Anderes Gerät? QR-Code anzeigen</LinkKnopf>
      {offen && (
        <div className="space-y-4">
          <NummernListe punkte={VERBINDEN_SCHRITTE} testid="zwei-faktor-verbinden-schritte" />
          <QrBild src={daten.qr_code} />
        </div>
      )}
    </div>
  );
};

export const Assistent = ({ zwischenToken, startDaten, konto, onFertig, onAbbrechen, onCodesSichtbar, onPruefen, email }) => {
  const [merken, setMerken] = useState(true);
  const { uebernehmeToken, kontoNeuLaden } = useAuth();
  const art = useMemo(() => handyArt(navigator.userAgent, navigator.maxTouchPoints), []);
  const [schritt, setSchritt] = useState(1);
  const [daten, setDaten] = useState(startDaten || null);
  const [laedt, setLaedt] = useState(!startDaten);
  const [startFehler, setStartFehler] = useState('');
  const [code, setCode] = useState('');
  const [fehler, setFehler] = useState('');
  const [fehlversuche, setFehlversuche] = useState(0);
  const [pruefe, setPruefe] = useState(false);
  const [ergebnis, setErgebnis] = useState(null);
  const feldRef = useRef(null);
  const gestartet = useRef(Boolean(startDaten));
  const abbrechenRef = useRef(onAbbrechen);
  abbrechenRef.current = onAbbrechen;

  // 401 mit Zwischen-Token: Anmeldung abgelaufen -> zurück zur Passworteingabe.
  const abgelaufen = useCallback((err) => {
    if (zwischenToken && err?.response?.status === 401) {
      if (abbrechenRef.current) abbrechenRef.current(err);
      return true;
    }
    return false;
  }, [zwischenToken]);

  const starten = useCallback(async () => {
    setLaedt(true);
    setStartFehler('');
    try {
      const res = zwischenToken
        ? await api.post('/auth/2fa/einrichten/start', { zwischen_token: zwischenToken })
        : await api.post('/auth/me/2fa/einrichten/start');
      setDaten(res.data);
    } catch (err) {
      if (abgelaufen(err)) return;
      // 400: z. B. 2FA schon aktiv (veralteter Tab) – Meldung zeigen, Stand neu lesen.
      const abgelehnt = err?.response?.status === 400;
      setStartFehler(abgelehnt ? fehlertext(err, START_FEHLER) : START_FEHLER);
      if (abgelehnt && !zwischenToken) kontoNeuLaden();
    } finally {
      setLaedt(false);
    }
  }, [zwischenToken, abgelaufen, kontoNeuLaden]);

  // StrictMode führt Effekte doppelt aus: der Ref sorgt für genau EIN start.
  useEffect(() => {
    if (gestartet.current) return;
    gestartet.current = true;
    starten();
  }, [starten]);

  const bestaetigen = async (wert = code) => {
    if (!/^[0-9]{6}$/.test(wert) || pruefe) return;
    setPruefe(true);
    if (onPruefen) onPruefen(true);
    setFehler('');
    try {
      const res = zwischenToken
        ? await api.post('/auth/2fa/einrichten/bestaetigen', { zwischen_token: zwischenToken, code: wert, geraet_merker: merkerFuer(email), geraet_merken: merken })
        : await api.post('/auth/me/2fa/einrichten/bestaetigen', { code: wert });
      if (!zwischenToken) uebernehmeToken(res.data.access_token);
      else merkerSpeichern(email, res.data?.geraet_merker);
      setErgebnis(res.data);
      setSchritt(4);
      if (onCodesSichtbar) onCodesSichtbar(true);
    } catch (err) {
      if (abgelaufen(err)) return;
      setFehlversuche((n) => n + 1);
      setFehler(fehlertext(err, FALSCH));
      setCode('');
      setTimeout(() => feldRef.current && feldRef.current.focus(), 0);
    } finally {
      setPruefe(false);
      if (onPruefen) onPruefen(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="zwei-faktor-assistent">
      <SchrittLeiste schritt={schritt} />
      <p className="sr-only" data-testid="zwei-faktor-schritt-titel">{TITEL[schritt]}</p>

      {schritt === 1 && <AppAnleitung art={art} onSchonDa={() => setSchritt(2)} />}

      {schritt === 2 && (
        <div className="space-y-4" data-testid="zwei-faktor-schritt-2">
          <h3 className="font-semibold text-slate-900 font-[Manrope]">Mit der App verbinden</h3>
          {laedt && <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-blue-800" aria-label="Wird geladen" /></div>}
          {!laedt && startFehler && (
            <div className="space-y-3 text-center" data-testid="zwei-faktor-start-fehler">
              <p className="text-sm text-red-600" data-testid="zwei-faktor-start-fehler-text">{startFehler}</p>
              <Button type="button" variant="outline" onClick={starten} data-testid="zwei-faktor-erneut">Erneut versuchen</Button>
            </div>
          )}
          {!laedt && daten && <Verbinden daten={daten} art={art} />}
        </div>
      )}

      {schritt === 3 && (
        <div className="space-y-4" data-testid="zwei-faktor-schritt-3">
          <p className="text-sm text-slate-700">{ZAHL_TEXT}</p>
          {zwischenToken && <GeraetMerken merken={merken} onChange={setMerken} />}
          <CodeFelder wert={code} onChange={setCode} onComplete={bestaetigen} feldRef={feldRef} disabled={pruefe} />
          {fehler && <p className="text-center text-sm text-red-600" role="alert" data-testid="zwei-faktor-fehler">{fehler}</p>}
          {fehlversuche >= 2 && <p className="text-sm text-slate-600" data-testid="zwei-faktor-uhrzeit-hinweis"><Fett text={UHRZEIT_HINWEIS} /></p>}
        </div>
      )}

      {schritt === 4 && ergebnis && (
        <div className="space-y-4">
          <div className="flex items-start gap-2 rounded-lg bg-green-50 p-3 text-sm text-green-800" data-testid="zwei-faktor-geschafft">
            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" />
            <span><Fett text={GESCHAFFT} /></span>
          </div>
          <CodesAnzeige codes={ergebnis.wiederherstellungscodes} konto={konto || ergebnis.user?.email} onFertig={() => onFertig(ergebnis)} />
        </div>
      )}

      {schritt < 4 && (
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          {onAbbrechen && <Button type="button" variant="ghost" disabled={pruefe} onClick={() => onAbbrechen()} data-testid="zwei-faktor-abbrechen">Abbrechen</Button>}
          {schritt > 1 && <Button type="button" variant="outline" disabled={pruefe} onClick={() => { setFehler(''); setSchritt(schritt - 1); }} data-testid="zwei-faktor-zurueck">Zurück</Button>}
          {schritt < 3 && (
            <Button type="button" className="bg-blue-800 hover:bg-blue-900" disabled={schritt === 2 && !daten} onClick={() => setSchritt(schritt + 1)} data-testid="zwei-faktor-weiter">Weiter</Button>
          )}
          {schritt === 3 && (
            <Button type="button" className="bg-blue-800 hover:bg-blue-900" disabled={pruefe || !/^[0-9]{6}$/.test(code)} onClick={() => bestaetigen()} data-testid="zwei-faktor-bestaetigen">
              {pruefe ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Wird geprüft...</> : 'Bestätigen'}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default Assistent;
